import React, { useState, useEffect, useRef } from "react";
import {
  X,
  ChevronLeft,
  MoreHorizontal,
  ChevronRight,
  Copy,
} from "lucide-react";
import noImage from "../assets/img/brand/noImage.png";
import apiHelper from "../apiHelper";
import { formatDateTime } from "../utils/formateDate";
import { useHistory, useLocation } from "react-router-dom";
import noBrandImage from "../assets/img/brand/no-brand-img.png";
import AlertNotification from "../components/AlertNotification";
import ConfirmDisableModal from "../components/ConfirmDisableModal";

const SkuInfoDrawer = ({
  selectedRow,
  setSelectedRow,
  setSkuDrawerOpen,
  onEnableDisable,
  setDrawerOpen,
}) => {
  const [skuData, setSkuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skuTab, setSkuTab] = useState("general"); // ⬅ tabs: general / configuration
  const [toolbarDropdownOpen, setToolbarDropdownOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const drawerRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const activeTab = location.state?.activeTab || "spu";
  const [copied, setCopied] = useState(false);
  const [actionSkuId, setActionSkuId] = useState(null);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    recordName: "",
    action: "",
  });
  //   constend

  //constfunction
  const getSpecValue = (key) => {
    return skuData?.specs?.find(s => s.name.toLowerCase() === key.toLowerCase())?.value || "NA";
  };

  const fallbackCopyTextToClipboard = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error("Fallback copy failed");
        alert("Failed to copy text. Please copy manually.");
      }
    } catch (err) {
      console.error("Fallback copy error:", err);
      alert("Failed to copy text. Please copy manually.");
    }
  };

  const getConfigurationSpecs = () => {
    console.log('--------------------------');
    console.log(skuData?.configuration);
    if (!skuData?.configuration?.specifications) return [];
    return skuData.configuration.specifications;
  };

  // Get current SKU specifications for drawer
  const getCurrentSKUSpecifications = () => {
    // selectedRow from SpuSkuPage carries .specifications directly
    if (selectedRow?.specifications?.length) {
      return getSKUSpecifications(selectedRow);
    }
    // SKU has a configuration — use its specifications
    const configSpecs = skuData?.configuration?.specifications;
    if (configSpecs?.length) {
      return configSpecs.map(spec => ({
        name: spec.attribute_name || spec.name || '',
        value: spec.attribute_value || spec.value || '',
        id: spec.specification_id,
      }));
    }
    // SKU has no configuration — fall back to attributes mapped as specs
    if (skuData?.specs?.length) {
      return skuData.specs;
    }
    return [];
  };

  // Get specifications for a SKU (for drawer display)
  const getSKUSpecifications = (sku) => {
    if (!sku?.specifications || !Array.isArray(sku.specifications)) return [];

    return sku.specifications.map((spec) => ({
      name: spec.attribute_name,
      value: spec.attribute_value,
      id: spec.specification_id,
    }));
  };

  // Handler for confirming disable (from modal)
  const handleConfirmDisable = async () => {
    await handleSkuStatusChange(actionSkuId, false);
  };

  const handleCopy = async (textToCopy) => {
    if (!textToCopy) {
      console.error("No text to copy");
      alert("Failed to copy: No text available.");
      return;
    }

    if (
      window.isSecureContext &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Clipboard API failed:", err);
        fallbackCopyTextToClipboard(textToCopy);
      }
    } else {
      console.warn(
        "Clipboard API not available or not in secure context, using fallback"
      );
      fallbackCopyTextToClipboard(textToCopy);
    }
  };

  // Handler for SKU enable/disable actions
  const handleSkuEnableDisableClick = async (skuId) => {
    const skuToUpdate = skuData?.sku_id === skuId ? skuData : null;
    if (!skuToUpdate) {
      console.error("SKU not found");
      return;
    }

    const isEnabled =
      skuToUpdate.enable_display === true ||
      skuToUpdate.enable_display === "true" ||
      skuToUpdate.enable_display === "Enabled" ||
      skuToUpdate.enable_display === 1;

    if (isEnabled) {
      setActionSkuId(skuId);
      setDisableModalOpen(true);
    } else {
      await handleSkuStatusChange(skuId, true);
    }
  };

  // Handler for actual SKU status change
  const handleSkuStatusChange = async (skuId, enable) => {
    console.log(">>>>>>");
    console.log(skuId);
    console.log(enable);
    try {
      const response = await apiHelper(
        `inventory/sku/${skuId}/update/`,
        "PUT",
        { enable_display: enable }
      );

      if (response.ok) {
        setSkuData((prev) => ({
          ...prev,
          enable_display: enable,
        }));

        if (selectedRow && selectedRow.sku_id === skuId) {
          setSelectedRow((prev) => ({
            ...prev,
            enable_display: enable,
          }));
        }

        setNotification({
          show: true,
          type: "success",
          recordName: "SKU",
          action: enable ? "enable" : "disable",
        });

        setTimeout(() => {
          setNotification({
            show: false,
            type: "",
            recordName: "",
            action: "",
          });
        }, 5000);
      } else {
        setNotification({
          show: true,
          type: "error",
          recordName: "SKU",
          action: enable ? "enable" : "disable",
        });
      }
    } catch (error) {
      console.error("Error updating SKU status:", error);
      setNotification({
        show: true,
        type: "error",
        recordName: "SKU",
        action: "update",
      });
    } finally {
      setDisableModalOpen(false);
      setActionSkuId(null);
    }
  };

  // Handler for closing notification
  const handleCloseNotification = () => {
    setNotification({ show: false, type: "", recordName: "", action: "" });
  };

  const isBrandNameOverflowing = (brandName) => {
    if (!brandName) return false;
    return brandName.length > 30;
  };

  // useRef
  const toolbarRef = useRef(null);

  // ========================= 1️⃣ FETCH API =========================
  useEffect(() => {
    if (!selectedRow?.sku_id) return;

    async function fetchSku() {
      try {
        setLoading(true);
        const response = await apiHelper(
          `inventory/sku/drawer/${selectedRow.sku_id}/`
        );
        const data = await response.json();

        // =================  Normalize to UI shape =================
        setSkuData({
          ...data,
          specs:
            data.attributes?.map((a) => ({
              name: a.preset_name,
              value: a.option_value,
            })) || [],
          configuration: data.configuration
            ? {
                configuration_name: data.configuration.configuration_name,
                notes: data.configuration.notes,
                specifications: data.configuration.specifications || [],
                images: data.configuration.images || [],
                custom_fields: data.configuration.custom_fields || [],
              }
            : null,
          spu: data.spu
            ? {
                ...data.spu,
                archived_by_full_name: data.spu.archived_by || null,
                archived_at: data.spu.archived_at || null,
              }
            : null,
        });
      } catch (err) {
        console.error("❌ Failed to fetch SKU:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchSku();
  }, [selectedRow?.sku_id]);

  console.log("Selected Row >>>>>>>>.");
  console.log(selectedRow);
  console.log("SKU Data >>>>>>>>.");
  console.log(skuData);

  // ========================= UTILS =========================
  const toggleToolbarDropdown = () => {
    setToolbarDropdownOpen(!toolbarDropdownOpen);
  };

  const handleEnableDisable = () => {
    if (!skuData) return;
    // onEnableDisable?.(skuData.sku_id);
    if (onEnableDisable) {
      onEnableDisable(skuData.sku_id);
    }
    setToolbarDropdownOpen(false);
  };

  return (
    <>
      <div
        className="drawer-overlay"
        onMouseDown={(e) => {
          e.stopPropagation();
          setSkuDrawerOpen(false);
        }}
      />
      <div className="entity-drawer" ref={drawerRef}>
        <div className="drawer-header" style={{ background: "#F8F9FA" }}>
          <div className="d-flex align-items-center gap-1">
            <button
              className="back-btn"
              onClick={(e) => {
                e.stopPropagation();
                setSkuDrawerOpen(false);
                // setSelectedRow(null);
              }}
              style={{
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
                margin: 0,
              }}
            >
              <ChevronLeft size={24} />
            </button>

            <h3>SKU Info</h3>
          </div>
          <button
            className="close-btn"
            onClick={() => {
              setSkuDrawerOpen(false);
              if (setDrawerOpen) setDrawerOpen(false);
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="main-drawer-inner">
          <div
            className="drawer-title d-flex align-items-center justify-content-between"
            style={{
              borderBottom: "none",
              paddingBottom: "15px",
              boxShadow: "none",
              marginBottom: "0px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "nowrap",
              width: "100%",
            }}
          >
            <div className="d-flex align-items-center">
              <img
                src={
                  skuData?.configuration?.images?.length > 0
                    ? skuData.configuration.images[0].sku_image_name
                    : noImage
                }
                alt="avatar"
                className="avatar"
                style={{ width: 40, height: 40, marginRight: 8 }}
              />
              <h2 style={{ textTransform: "none" }} className="pb-0">
                {selectedRow?.variant || skuData?.sku_name || (
                  <span style={{ color: "#ADB5BD" }}>NA</span>
                )}
              </h2>
            </div>
            {/* <div className="entities-tab">
              <div className="toolbar">
                <div
                  className="toolbar-right"
                  style={{ position: "relative", flexWrap: "nowrap" }}
                  ref={toolbarRef}
                >
                  {activeTab == "spu" && (
                    <button
                      className="drawer-dots"
                      style={{ boxShadow: "none" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleToolbarDropdown();
                      }}
                    >
                      <MoreHorizontal size={18} />
                    </button>
                  )}
                  {toolbarDropdownOpen && (
                    <div
                      className="backdrop-cls"
                      style={{
                        position: "absolute",
                        top: 40,
                        right: 0,
                        background: "white",
                        boxShadow: "0px 1px 4px 0px #0000001A",
                        zIndex: 1000,
                        minWidth: 152,
                        borderRadius: 8,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="btn-align-forall">
                        <div className="common-for-all">
                          <button
                            onClick={() => {
                              if (skuData) {
                                handleSkuEnableDisableClick(skuData?.sku_id);
                              }
                              setToolbarDropdownOpen(false);
                            }}
                          >
                            {skuData?.enable_display === true ||
                            skuData?.enable_display === "true" ||
                            skuData?.enable_display === "Enabled" ||
                            skuData?.enable_display === 1
                              ? "Disable"
                              : "Enable"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div> */}
          </div>

          <div
            className="drawer-tabs"
            style={{
              padding: "10px",
              borderBottom: "none",
              gap: "20px",
              paddingTop: "40px",
            }}
          >
            <button
              className={skuTab === "general" ? "active" : ""}
              onClick={() => setSkuTab("general")}
            >
              General
            </button>
            <button
              className={skuTab === "configuration" ? "active" : ""}
              onClick={() => setSkuTab("configuration")}
            >
              Configuration
            </button>
          </div>

          <div className="drawer-content">
            {skuTab === "general" && (
              <div className="tab-panel general-panel">
                <div
                  className="card-section"
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    overflow: "hidden",
                    marginBottom: "20px",
                    marginTop: "20px",
                  }}
                >
                  <div
                    className="card-header"
                    style={{
                      backgroundColor: " #F8F9FA",
                      padding: "15px 20px",
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: " #212529",
                      display: "flex",
                      alignItems: "center",
                      alignSelf: "stretch",
                      borderRadius: "12px 12px 0px 0px",
                      gap: "10px",
                      height: "50px",
                      borderBottom: "none",
                    }}
                  >
                    Basic Info
                  </div>
                  <div
                    className="card-body"
                    style={{
                      backgroundColor: "#FFFFFF",
                      padding: "20px",
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "20px",
                    }}
                  >
                    <div
                      className="field-block"
                      style={{
                        borderBottom: "none",
                        display: "flex",
                        flexDirection: "column",
                        padding: "0px",
                        gridColumn: "1",
                        gridRow: "1",
                      }}
                    >
                      <div className="field-label">SKU Name</div>
                      <div
                        className="field-value"
                        style={{
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {selectedRow?.variant || skuData?.sku_name || (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>
                    <div
                      className="field-block"
                      style={{
                        borderBottom: "none",
                        display: "flex",
                        flexDirection: "column",
                        padding: "0px",
                        gridColumn: "2",
                        gridRow: "1",
                      }}
                    >
                      <div className="field-label">Condition L1</div>
                      <div className="field-value">
                        {selectedRow?.condition || skuData?.condition ? (
                          <span
                            style={{
                              background: "#FFEB00",
                              borderRadius: "999px",
                              fontWeight: 600,
                              fontSize: "13px",
                              color: "#000",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              whiteSpace: "nowrap",
                              width: 72,
                              height: 20,
                              boxSizing: "border-box",
                            }}
                          >
                            {selectedRow?.condition || skuData?.condition}
                          </span>
                        ) : (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>
                    <div
                      className="field-block"
                      style={{
                        borderBottom: "none",
                        display: "flex",
                        flexDirection: "column",
                        padding: "0px",
                      }}
                    >
                      <div className="field-label">SKU Code</div>
                      <div
                        className="field-value"
                        style={{
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {selectedRow?.sku_code || skuData?.sku_code || (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>
                    <div
                      className="field-block"
                      style={{
                        borderBottom: "none",
                        display: "flex",
                        flexDirection: "column",
                        padding: "0px",
                      }}
                    >
                      <div className="field-label">Model Number</div>
                      <div
                        className="field-value"
                        style={{
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {selectedRow?.model_number || skuData?.model_number || (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>
                    <div
                      className="field-block"
                      style={{
                        borderBottom: "none",
                        display: "flex",
                        flexDirection: "column",
                        padding: "0px",
                      }}
                    >
                      <div className="field-label">Enable</div>
                      <div className="field-value">
                        {(() => {
                          const raw = selectedRow?.enable_display ?? skuData?.enable_display;
                          const isEnabled =
                            raw === true || raw === "true" || raw === "Enabled" || raw === 1;
                          return (
                            <span style={{
                              display: "inline-block",
                              padding: "4px 14px",
                              borderRadius: 999,
                              border: isEnabled ? "1px solid #198754" : "1px solid #DC3545",
                              backgroundColor: isEnabled ? "#D1E7DD" : "#F8D7DA",
                              color: isEnabled ? "#198754" : "#DC3545",
                              fontWeight: 600,
                              fontSize: 12,
                            }}>
                              {isEnabled ? "YES" : "NO"}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    {/* Dynamic Specifications */}
                    {getCurrentSKUSpecifications().map((spec, index) => (
                      <div
                        key={index}
                        className="field-block"
                        style={{
                          borderBottom: "none",
                          display: "flex",
                          flexDirection: "column",
                          padding: "0px",
                        }}
                      >
                        <div className="field-label">{spec.name}</div>
                        <div className="field-value">
                          {spec.value ? (
                            <span
                              style={{
                                background: "#FFEB00",
                                padding: "6px 18px",
                                borderRadius: "20px",
                                fontWeight: 600,
                                fontSize: "13px",
                                color: "#000",
                                display: "inline-block",
                                minWidth: "60px",
                                textAlign: "center",
                              }}
                            >
                              {spec.value}
                            </span>
                          ) : (
                            <span style={{ color: "#ADB5BD" }}>NA</span>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* SIM Option */}
                    <div
                      className="field-block"
                      style={{
                        borderBottom: "none",
                        display: "flex",
                        flexDirection: "column",
                        padding: "0px",
                      }}
                    >
                      <div className="field-label">SIM Option</div>
                      <div className="field-value">
                        {selectedRow?.sim_option || skuData?.sim_option || (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>

                    {/* Set Type */}
                    <div
                      className="field-block"
                      style={{
                        borderBottom: "none",
                        display: "flex",
                        flexDirection: "column",
                        padding: "0px",
                      }}
                    >
                      <div className="field-label">Set Type</div>
                      <div className="field-value">
                        <span
                          style={{
                            background: "#FFEB00",
                            padding: "2px 10px",
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {selectedRow?.set_type || skuData?.set_type || "None"}
                        </span>
                      </div>
                    </div>

                    <div
                      className="field-block"
                      style={{
                        borderBottom: "none",
                        padding: "9px 0",
                        gridColumn: "1 / -1",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        width: "100%",
                        padding: "0px",
                      }}
                    >
                      <div
                        className="field-label"
                        style={{ marginBottom: "4px" }}
                      >
                        Open ID
                      </div>
                      <div
                        className="field-value"
                        style={{ width: "100%", wordBreak: "break-all" }}
                      >
                        {selectedRow?.openId || skuData?.uuid ? (
                          <div className="field-value d-flex align-items-center gap-2">
                            <span
                              style={{
                                color: "#2C7BE5",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                              onClick={() =>
                                handleCopy(selectedRow?.openId || skuData?.uuid)
                              }
                            >
                              {selectedRow?.openId || skuData?.uuid}
                              <Copy
                                size={13}
                                onClick={() => handleCopy(selectedRow?.openId)}
                                style={{
                                  cursor: "pointer",
                                  color: "#2C7BE5",
                                  marginLeft: "6px",
                                }}
                              />
                            </span>
                            {copied && (
                              <span
                                style={{ fontSize: "12px", color: "#2C7BE5" }}
                              >
                                Copied!
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>

                    <div
                      className="field-block"
                      style={{ borderBottom: "none", padding: "0px" }}
                    >
                      <div className="field-label">Created By</div>
                      <div className="field-value">
                        {selectedRow?.created_by_full_name || skuData?.created_by_full_name || (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>

                    <div
                      className="field-block"
                      style={{ borderBottom: "none", padding: "0px" }}
                    >
                      <div className="field-label">Created Time</div>
                      <div className="field-value">
                        {formatDateTime(selectedRow?.created_at || skuData?.created_at) || (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>

                    <div
                      className="field-block"
                      style={{ borderBottom: "none", padding: "0px" }}
                    >
                      <div className="field-label">Last Modified By</div>
                      <div className="field-value">
                        {selectedRow?.modified_by_full_name || skuData?.modified_by_full_name || (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>

                    <div
                      className="field-block"
                      style={{ borderBottom: "none", padding: "0px" }}
                    >
                      <div className="field-label">Last Modified Time</div>
                      <div className="field-value">
                        {formatDateTime(selectedRow?.modified_at || skuData?.modified_at) || (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>
                    {activeTab !== "spu" && (
                      <div
                        className="field-block"
                        style={{ borderBottom: "none", padding: "0px" }}
                      >
                        <div className="field-label">Archived By</div>
                        <div className="field-value">
                          {selectedRow.archived_by_full_name || (
                            <span style={{ color: "#ADB5BD" }}>NA</span>
                          )}
                        </div>
                      </div>
                    )}
                    {activeTab !== "spu" && (
                      <div
                        className="field-block"
                        style={{ borderBottom: "none", padding: "0px" }}
                      >
                        <div className="field-label">Archived Time</div>
                        <div className="field-value">
                          {formatDateTime(selectedRow?.archived_at) || (
                            <span style={{ color: "#ADB5BD" }}>NA</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* ===== Business INFO CARD ===== */}
                <div
                  className="card-section"
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    className="card-header"
                    style={{
                      backgroundColor: "#F8F9FA",
                      padding: "10px 16px",
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#212529",
                      borderBottom: "none",
                    }}
                  >
                    Business Info
                  </div>

                  <div
                    className="card-body"
                    style={{
                      backgroundColor: "#FFFFFF",
                      padding: "10px 20px",
                      display: "grid",
                      gridTemplateColumns: "minmax(150px, 1fr) 1fr",
                      gap: "15px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    <div
                      className="field-block"
                      style={{
                        borderBottom: "none",
                        padding: "0px 10px",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      <div className="field-label">GTIN</div>
                      <div className="field-value">
                        {skuData?.gtin || (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>
                    <div
                      className="field-block"
                      style={{
                        borderBottom: "none",
                        padding: "0px 10px",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      <div className="field-label">MSRP</div>
                      <div className="field-value">
                        {skuData?.msrp ? (
                          `SGD $${skuData.msrp}`
                        ) : (
                          <span style={{ color: "#ADB5BD" }}>NA</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {skuTab === "configuration" && (
              <div className="tab-panel configuration-panel">
                {skuData?.configuration ? (
                  <>
                    <div
                      className="card-section"
                      style={{
                        borderRadius: "12px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                        marginTop: "40px",
                      }}
                    >
                      <div
                        className="card-header"
                        style={{
                          backgroundColor: "#F8F9FA",
                          padding: "10px 16px",
                          fontFamily: "Montserrat",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#212529",
                          borderBottom: "none",
                        }}
                      >
                        Configuration Name
                      </div>

                      <div
                        className="card-body"
                        style={{
                          backgroundColor: "#FFFFFF",
                          padding: "16px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        <div className="field-block" style={{ padding: "0px" }}>
                          <div className="field-value">
                            {skuData.configuration.configuration_name || (
                              <span style={{ color: "#ADB5BD" }}>NA</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className="card-section"
                      style={{
                        borderRadius: "12px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                        marginTop: "20px",
                      }}
                    >
                      <div
                        className="card-header"
                        style={{
                          backgroundColor: "#F8F9FA",
                          padding: "10px 16px",
                          fontFamily: "Montserrat",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#212529",
                          borderBottom: "none",
                        }}
                      >
                        Customised Info
                      </div>

                      <div
                        className="card-body"
                        style={{
                          backgroundColor: "#FFFFFF",
                          padding: "16px",
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: "16px",
                        }}
                      >
                        {getConfigurationSpecs().map((spec, index) => (
                          <div
                            key={index}
                            className="field-block"
                            style={{ borderBottom: "none", padding: "0px" }}
                          >
                            <div className="field-label">
                              {spec.attribute_name || "Specification"}
                            </div>
                            <div className="field-value">
                              {spec.attribute_value || (
                                <span style={{ color: "#ADB5BD" }}>NA</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Images Section */}
                    <div
                      className="card-section"
                      style={{
                        borderRadius: "12px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                        marginTop: "20px",
                      }}
                    >
                      <div
                        className="card-header"
                        style={{
                          backgroundColor: "#F8F9FA",
                          padding: "10px 16px",
                          fontFamily: "Montserrat",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#212529",
                          borderBottom: "none",
                        }}
                      >
                        Images
                      </div>
                      {(() => {
                        const hasImages =
                          Array.isArray(skuData.configuration.images) &&
                          skuData.configuration.images.length > 0;

                        return (
                          <div
                            className="field-value"
                            style={{
                              width: "100%",
                              display: "flex",
                              gap: 10,
                              flexWrap: "wrap",
                              padding: "10px",
                            }}
                          >
                            {!hasImages && (
                              <img
                                src={noImage}
                                alt="no image"
                                style={{
                                  width: 120,
                                  height: 120,
                                  objectFit: "cover",
                                  borderRadius: 6,
                                  opacity: 0.7,
                                }}
                                onClick={() => {
                                  setPreviewImages([noImage]);
                                  setPreviewIndex(0);
                                  setImageModalOpen(true);
                                }}
                              />
                            )}

                            {hasImages &&
                              skuData?.configuration?.images?.map(
                                (imgObj, index) => {
                                  const imageUrl = imgObj?.sku_image_name;
                                  return (
                                    <img
                                      key={imgObj?.sku_configuration_image_id}
                                      src={imageUrl}
                                      alt="configuration"
                                      style={{
                                        width: 120,
                                        height: 120,
                                        objectFit: "cover",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                      }}
                                      onClick={() => {
                                        // For preview, use backend URLs too
                                        const previewList =
                                          skuData.configuration.images.map(
                                            (img) => img.sku_image_name
                                          );

                                        setPreviewImages(previewList);
                                        setPreviewIndex(index);
                                        setImageModalOpen(true);
                                      }}
                                      onError={(e) => (e.target.src = noImage)}
                                    />
                                  );
                                }
                              )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Custom Fields Section */}
                    {skuData.configuration.custom_fields &&
                      skuData.configuration.custom_fields.length > 0 && (
                        <div
                          className="card-section"
                          style={{
                            borderRadius: "12px",
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                            overflow: "hidden",
                            marginTop: "20px",
                          }}
                        >
                          <div
                            className="card-header"
                            style={{
                              backgroundColor: "#F8F9FA",
                              padding: "10px 16px",
                              fontFamily: "Montserrat",
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#212529",
                              borderBottom: "none",
                            }}
                          >
                            Custom Fields
                          </div>

                          <div
                            className="card-body"
                            style={{
                              backgroundColor: "#FFFFFF",
                              padding: "16px",
                              display: "grid",
                              gridTemplateColumns: "repeat(2, 1fr)",
                              gap: "16px",
                            }}
                          >
                            {skuData.configuration.custom_fields.map(
                              (field, index) => (
                                <div
                                  key={index}
                                  className="field-block"
                                  style={{
                                    borderBottom: "none",
                                    padding: "0px",
                                  }}
                                >
                                  <div className="field-label">
                                    {field.field_name || "Field"}
                                  </div>
                                  <div className="field-value">
                                    {field.field_data || (
                                      <span style={{ color: "#ADB5BD" }}>
                                        NA
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {/* Notes Section */}
                    <div
                      className="card-section"
                      style={{
                        borderRadius: "12px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                        marginTop: "20px",
                      }}
                    >
                      <div
                        className="card-header"
                        style={{
                          backgroundColor: "#F8F9FA",
                          padding: "10px 16px",
                          fontFamily: "Montserrat",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#212529",
                          borderBottom: "none",
                        }}
                      >
                        Notes
                      </div>

                      <div
                        className="card-body"
                        style={{
                          backgroundColor: "#FFFFFF",
                          padding: "16px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        <div className="field-block" style={{ padding: "0px" }}>
                          <div
                            className="field-value"
                            style={{
                              background: "#FFFFFF",
                              fontSize: "13px",
                              color: skuData.configuration.notes
                                ? "#212529"
                                : "#ADB5BD",
                              minHeight: "24px",
                            }}
                          >
                            {skuData.configuration.notes || "NA"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-content">
                      <div class="icon">
                        <img
                          src={noBrandImage}
                          alt="avatar"
                          className="avatar"
                          style={{
                            width: 120,
                            height: 120,
                            verticalAlign: "middle",
                          }}
                        />
                      </div>
                      <h1 class="no-item-title mt-2">
                        No Configuration Applied
                      </h1>
                      <p class="no-item-text">
                        No Configuration Applied to this SKU
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
            {imageModalOpen && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,0.35)", // soft background blur/dim
                  backdropFilter: "blur(3px)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 5000,
                }}
              >
                {/* WHITE CARD */}
                <div
                  style={{
                    background: "#fff",
                    borderRadius: "16px",
                    padding: "24px",
                    maxWidth: "650px",
                    maxHeight: "80vh",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {/* IMAGE */}
                  <img
                    src={previewImages[previewIndex]}
                    alt="preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "70vh",
                      objectFit: "contain",
                      borderRadius: "12px",
                    }}
                  />

                  {/* CLOSE BUTTON */}
                  {/* CLOSE BUTTON – top-right of screen */}
                  <button
                    onClick={() => setImageModalOpen(false)}
                    style={{
                      position: "fixed",
                      top: "0px",
                      right: "0px",
                      background: "white",
                      // borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "none",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      cursor: "pointer",
                      zIndex: 6000,
                    }}
                  >
                    <X size={22} />
                  </button>
                </div>

                {/* LEFT NAV */}
                {previewImages.length > 1 && (
                  <button
                    onClick={() =>
                      setPreviewIndex(
                        (i) =>
                          (i - 1 + previewImages.length) % previewImages.length
                      )
                    }
                    style={{
                      position: "fixed",
                      left: "0px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "white",
                      // borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "none",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                      cursor: "pointer",
                    }}
                  >
                    <ChevronLeft size={28} />
                  </button>
                )}

                {/* RIGHT NAV */}
                {previewImages.length > 1 && (
                  <button
                    onClick={() =>
                      setPreviewIndex((i) => (i + 1) % previewImages.length)
                    }
                    style={{
                      position: "fixed",
                      right: "0px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "white",
                      // borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "none",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                      cursor: "pointer",
                    }}
                  >
                    <ChevronRight size={28} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {notification.show && (
          <AlertNotification
            type={notification.type}
            recordName={notification.recordName}
            action={notification.action}
            onClose={handleCloseNotification}
          />
        )}

        <ConfirmDisableModal
          isOpen={disableModalOpen}
          onCancel={() => {
            setDisableModalOpen(false);
            setActionSkuId(null);
          }}
          onConfirm={handleConfirmDisable}
          entityName="SKU"
        />
      </div>
    </>
  );
};

export default SkuInfoDrawer;
